if (!customElements.get('footer-help-center')) {
  class FooterHelpCenter extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.launcher = this.querySelector('[data-help-launcher]');
      this.panel = this.querySelector('[data-help-panel]');
      this.closeButton = this.querySelector('[data-help-close]');
      this.searchForm = this.querySelector('[data-help-search-form]');
      this.searchInput = this.querySelector('[data-help-search]');
      this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
      this.initialFaqLimit = 5;
      this.emptyState = this.querySelector('[data-help-empty]');
      this.resultStatus = this.querySelector('[data-help-result-status]');
      this.faqView = this.querySelector('[data-help-faq-view]');
      this.contactView = this.querySelector('[data-help-contact-view]');
      this.contactOpenButton = this.querySelector('[data-help-contact-open]');
      this.backButton = this.querySelector('[data-help-back]');
      this.mainTitle = this.querySelector('[data-help-main-title]');
      this.contactTitle = this.querySelector('.footer-help-center__contact-title');
      this.contactForm = this.querySelector('.footer-help-center__form');
      this.contactStatus = this.querySelector('[data-help-contact-status]');
      this.topicSelect = this.querySelector('[data-help-topic-select]');
      this.topicToggle = this.querySelector('[data-help-topic-toggle]');
      this.topicMenu = this.querySelector('[data-help-topic-menu]');
      this.topicInput = this.querySelector('[data-help-topic-input]');
      this.topicValue = this.querySelector('[data-help-topic-value]');
      this.topicError = this.querySelector('[data-help-topic-error]');
      this.topicOptions = Array.from(this.querySelectorAll('[data-help-topic-option]'));
      this.currentView = 'faq';
      this.abortController = new AbortController();
      this.showDelay = Math.max(0, Number.parseInt(this.dataset.helpShowDelay || '0', 10) || 0);

      if (!this.launcher || !this.panel) return;

      this.revealTimer = window.setTimeout(() => {
        if (this.isConnected) this.hidden = false;
      }, this.showDelay);

      window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.panel) return;
        this.panel.hidden = false;
      });

      const { signal } = this.abortController;

      this.launcher.addEventListener('click', () => this.open(), { signal });
      this.closeButton?.addEventListener('click', () => this.close(), { signal });
      this.contactOpenButton?.addEventListener('click', () => this.showContactView(), { signal });
      this.backButton?.addEventListener('click', () => this.showFaqView(), { signal });
      this.topicToggle?.addEventListener('click', () => this.toggleTopicMenu(), { signal });
      this.topicToggle?.addEventListener('keydown', (event) => this.handleTopicToggleKeydown(event), { signal });
      this.topicOptions.forEach((option) => {
        option.addEventListener('click', () => this.selectTopic(option), { signal });
        option.addEventListener('keydown', (event) => this.handleTopicOptionKeydown(event, option), { signal });
      });
      document.addEventListener(
        'click',
        (event) => {
          if (!this.topicSelect?.contains(event.target)) this.closeTopicMenu();
        },
        { signal }
      );
      this.contactForm?.addEventListener(
        'submit',
        (event) => {
          if (this.topicInput && !this.topicInput.value) {
            event.preventDefault();
            this.closeTopicMenu();
            this.topicToggle?.setAttribute('aria-invalid', 'true');
            if (this.topicError) this.topicError.hidden = false;
            this.topicToggle?.focus();
            return;
          }

          try {
            window.sessionStorage.setItem('footer-help-center-contact-submitted', 'true');
          } catch (error) {
            // The form still submits when storage is unavailable.
          }
        },
        { signal }
      );
      this.searchForm?.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();
          this.filterItems();
          this.searchInput?.focus();
        },
        { signal }
      );
      this.searchInput?.addEventListener('input', () => this.filterItems(), { signal });
      this.panel.addEventListener('keydown', (event) => this.handlePanelKeydown(event), { signal });
      this.filterItems();

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!this.contains(event.target)) return;

          this.open(false);
          const selectedItem = event.target.closest('[data-faq-item]');
          if (selectedItem) selectedItem.open = true;
        },
        { signal }
      );

      let restoreContactView = false;
      try {
        restoreContactView =
          Boolean(this.contactStatus) &&
          window.sessionStorage.getItem('footer-help-center-contact-submitted') === 'true';
        if (restoreContactView) {
          window.sessionStorage.removeItem('footer-help-center-contact-submitted');
        }
      } catch (error) {
        restoreContactView = false;
      }

      if (restoreContactView) {
        window.requestAnimationFrame(() => {
          this.showContactView(false);
          this.open(false);
          this.contactStatus.focus();
        });
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.revealTimer);
      document.documentElement.classList.remove('footer-help-center-is-open');
      this.isInitialized = false;
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .trim();
    }

    open(moveFocus = true) {
      if (!this.classList.contains('is-open')) {
        this.previouslyFocused = document.activeElement;
      }
      this.panel.hidden = false;
      this.classList.add('is-open');
      this.launcher.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('footer-help-center-is-open');

      if (moveFocus) {
        window.setTimeout(() => {
          if (this.currentView === 'contact') {
            this.contactTitle?.focus();
          } else {
            this.searchInput?.focus();
          }
        }, 120);
      }
    }

    close() {
      this.closeTopicMenu();
      this.classList.remove('is-open');
      this.launcher.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('footer-help-center-is-open');
      this.showFaqView(false);

      if (this.previouslyFocused instanceof HTMLElement) {
        this.previouslyFocused.focus();
      } else {
        this.launcher.focus();
      }
    }

    showContactView(moveFocus = true) {
      if (!this.contactView || !this.faqView) return;

      this.closeTopicMenu();
      this.currentView = 'contact';
      this.classList.add('is-contact-view');
      this.faqView.hidden = true;
      this.contactView.hidden = false;
      this.backButton.hidden = false;
      this.panel.setAttribute('aria-labelledby', this.contactTitle?.id || this.mainTitle?.id || '');
      this.querySelector('.footer-help-center__body')?.scrollTo({ top: 0, behavior: 'auto' });

      if (moveFocus) {
        window.setTimeout(() => this.contactTitle?.focus(), 0);
      }
    }

    showFaqView(moveFocus = true) {
      if (!this.contactView || !this.faqView) return;

      this.closeTopicMenu();
      this.currentView = 'faq';
      this.classList.remove('is-contact-view');
      this.contactView.hidden = true;
      this.faqView.hidden = false;
      this.backButton.hidden = true;
      this.panel.setAttribute('aria-labelledby', this.mainTitle?.id || '');
      this.querySelector('.footer-help-center__body')?.scrollTo({ top: 0, behavior: 'auto' });

      if (moveFocus && this.classList.contains('is-open')) {
        window.setTimeout(() => this.searchInput?.focus(), 0);
      }
    }

    filterItems() {
      const query = this.normalize(this.searchInput?.value);
      const isSearching = query.length > 0;
      let visibleCount = 0;

      this.items.forEach((item, index) => {
        const matchesQuery = !isSearching || this.normalize(item.dataset.searchText).includes(query);
        const isVisible = matchesQuery && (isSearching || index < this.initialFaqLimit);
        item.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      if (this.emptyState) this.emptyState.hidden = visibleCount !== 0;
      if (this.resultStatus) {
        const singular = this.resultStatus.dataset.singular || 'article';
        const plural = this.resultStatus.dataset.plural || 'articles';
        this.resultStatus.textContent = `${visibleCount} ${visibleCount === 1 ? singular : plural}`;
      }
    }

    toggleTopicMenu() {
      if (this.topicMenu?.hidden === false) {
        this.closeTopicMenu();
      } else {
        this.openTopicMenu();
      }
    }

    openTopicMenu(moveFocus = false) {
      if (!this.topicSelect || !this.topicToggle || !this.topicMenu || !this.topicOptions.length) return;

      this.topicMenu.hidden = false;
      this.topicSelect.classList.add('is-open');
      this.topicToggle.setAttribute('aria-expanded', 'true');

      if (moveFocus) {
        const selectedOption =
          this.topicOptions.find((option) => option.getAttribute('aria-selected') === 'true') || this.topicOptions[0];
        selectedOption?.focus();
      }
    }

    closeTopicMenu(moveFocus = false) {
      if (!this.topicSelect || !this.topicToggle || !this.topicMenu) return;

      this.topicMenu.hidden = true;
      this.topicSelect.classList.remove('is-open');
      this.topicToggle.setAttribute('aria-expanded', 'false');
      if (moveFocus) this.topicToggle.focus();
    }

    selectTopic(option) {
      if (!option || !this.topicInput || !this.topicValue) return;

      const value = option.dataset.value || option.textContent.trim();
      this.topicInput.value = value;
      this.topicValue.textContent = option.textContent.trim();
      this.topicOptions.forEach((item) => {
        item.setAttribute('aria-selected', item === option ? 'true' : 'false');
      });
      this.topicToggle?.removeAttribute('aria-invalid');
      if (this.topicError) this.topicError.hidden = true;
      this.closeTopicMenu(true);
    }

    handleTopicToggleKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      this.openTopicMenu(true);
    }

    handleTopicOptionKeydown(event, option) {
      const optionIndex = this.topicOptions.indexOf(option);
      let nextIndex = optionIndex;

      if (event.key === 'ArrowDown') nextIndex = Math.min(optionIndex + 1, this.topicOptions.length - 1);
      else if (event.key === 'ArrowUp') nextIndex = Math.max(optionIndex - 1, 0);
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = this.topicOptions.length - 1;
      else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeTopicMenu(true);
        return;
      } else {
        return;
      }

      event.preventDefault();
      this.topicOptions[nextIndex]?.focus();
    }

    handlePanelKeydown(event) {
      if (event.key === 'Escape') {
        if (this.topicMenu?.hidden === false) {
          event.preventDefault();
          this.closeTopicMenu(true);
          return;
        }

        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = Array.from(
        this.panel.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], summary, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hidden && element.offsetParent !== null);

      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  customElements.define('footer-help-center', FooterHelpCenter);
}
